import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';
import { useMode, MODES } from '../contexts/ModeContext';

function KnowledgeCheck({ user }) {
  const navigate = useNavigate();
  const { currentMode, requestModeSwitch, isTrainingMode } = useMode();
  
  // State
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [useAIQuestions, setUseAIQuestions] = useState(false);

  // Fetch topics on mount
  useEffect(() => {
    fetchTopics();
    fetchHistory();
  }, []);

  // Auto-switch to Training Mode if in Reference Mode
  useEffect(() => {
    if (!isTrainingMode) {
      requestModeSwitch(MODES.TRAINING);
    }
  }, [isTrainingMode, requestModeSwitch]);

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API}/training/knowledge-check/topics`);
      setTopics(response.data.topics);
    } catch (err) {
      setError('Failed to load topics');
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/training/knowledge-check/history`);
      setHistory(response.data.history);
    } catch (err) {
      console.error('Failed to load history');
    }
  };

  const startQuiz = async (topicId) => {
    setLoading(true);
    setError('');
    setAnswers({});
    setResults(null);
    setCurrentQuestionIndex(0);

    try {
      let response;
      // AI questions not supported for "All Topics" - use standard questions
      if (useAIQuestions && topicId !== 'all') {
        response = await axios.post(`${API}/training/knowledge-check/generate/${topicId}`);
      } else {
        response = await axios.get(`${API}/training/knowledge-check/questions/${topicId}`);
      }
      
      setSelectedTopic({
        id: topicId,
        name: response.data.topic_name,
        isAIGenerated: response.data.is_ai_generated || false
      });
      setQuestions(response.data.questions);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answerIndex) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const goToPrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const submitQuiz = async () => {
    setLoading(true);
    setError('');

    try {
      const submission = {
        topic: selectedTopic.id,
        answers: Object.entries(answers).map(([questionId, selectedAnswer]) => ({
          question_id: questionId,
          selected_answer: selectedAnswer
        }))
      };

      // Debug logging removed — quiz submission path below
      const response = await axios.post(`${API}/training/knowledge-check/submit`, submission);
      setResults(response.data);
      fetchHistory(); // Refresh history
    } catch (err) {
      console.error('Quiz submission error:', err);
      setError(err.response?.data?.detail || 'Failed to submit quiz');
    } finally {
      setLoading(false);
    }
  };

  const resetQuiz = () => {
    setSelectedTopic(null);
    setQuestions([]);
    setAnswers({});
    setResults(null);
    setCurrentQuestionIndex(0);
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 90) return '#22c55e'; // Green
    if (percentage >= 75) return '#3b82f6'; // Blue
    if (percentage >= 60) return '#f59e0b'; // Amber
    return '#ef4444'; // Red
  };

  // Topic Selection Screen
  if (!selectedTopic) {
    return (
      <div className="knowledge-check-page">
        <header className="kc-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1>Training Mode — Knowledge Checks</h1>
          </div>
          <div className="kc-mode-badge training">
            🟡 Training Mode — Not for Live Work
          </div>
        </header>

        <div className="kc-container">
          <div className="kc-intro">
            <h2>Choose a Learning Topic</h2>
            <p className="kc-disclaimer">
              Training Mode is for education only and is not authorized for live field work.
            </p>
          </div>

          {error && <div className="kc-error">{error}</div>}

          <div className="kc-options">
            <label className="kc-option-toggle">
              <input
                type="checkbox"
                checked={useAIQuestions}
                onChange={(e) => setUseAIQuestions(e.target.checked)}
              />
              <span>Use AI-Generated Questions</span>
            </label>
            {useAIQuestions && (
              <span className="kc-ai-note">(Not available for "All Topics")</span>
            )}
          </div>

          <div className="kc-topics-grid">
            {topics.map(topic => (
              <button
                key={topic.id}
                className={`kc-topic-card ${topic.id === 'all' ? 'kc-topic-all' : ''}`}
                onClick={() => startQuiz(topic.id)}
                disabled={loading}
              >
                <span className="kc-topic-icon">
                  {topic.id === 'all' && '📋'}
                  {topic.id === 'circuit_breakers' && '⚡'}
                  {topic.id === 'dc_systems' && '🔋'}
                  {topic.id === 'grounding' && '🔌'}
                  {topic.id === 'disconnects_isolation' && '🔒'}
                  {topic.id === 'protection_control' && '🛡️'}
                  {topic.id === 'safety_rules' && '⚠️'}
                  {topic.id === 'event_free' && '✅'}
                  {topic.id === 'escalation' && '📞'}
                </span>
                <span className="kc-topic-name">{topic.name}</span>
                <span className="kc-topic-count">{topic.question_count} questions</span>
              </button>
            ))}
          </div>

          <div className="kc-history-section">
            <button 
              className="btn-toggle-history"
              onClick={() => setShowHistory(!showHistory)}
            >
              {showHistory ? '▼ Hide History' : '▶ Show My History'}
            </button>

            {showHistory && history.length > 0 && (
              <div className="kc-history-list">
                {history.slice(0, 10).map((result, index) => (
                  <div key={index} className="kc-history-item">
                    <span className="kc-history-topic">{result.topic_name}</span>
                    <span 
                      className="kc-history-score"
                      style={{ color: getScoreColor(result.percentage) }}
                    >
                      {result.score}/{result.total_questions} ({result.percentage}%)
                    </span>
                    <span className="kc-history-label">{result.score_label}</span>
                    <span className="kc-history-date">
                      {new Date(result.completed_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {showHistory && history.length === 0 && (
              <p className="kc-no-history">No quiz history yet. Complete a knowledge check to see your progress.</p>
            )}
          </div>

          <div className="kc-footer-disclaimer">
            <p>
              <strong>⚠️ Training Reminder:</strong> This knowledge check is for training only. 
              It does not authorize work or replace procedures.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (results) {
    return (
      <div className="knowledge-check-page">
        <header className="kc-header">
          <button className="btn-back" onClick={resetQuiz}>
            ← Back to Topics
          </button>
          <h1>📊 Results — {selectedTopic.name}</h1>
          <div className="kc-mode-badge training">
            🟡 Training Mode
          </div>
        </header>

        <div className="kc-container">
          <div className="kc-results">
            <div 
              className="kc-score-circle"
              style={{ borderColor: getScoreColor(results.percentage) }}
            >
              <span className="kc-score-number">{results.score}/{results.total_questions}</span>
              <span className="kc-score-percent">{results.percentage}%</span>
            </div>

            <h2 
              className="kc-score-label"
              style={{ color: getScoreColor(results.percentage) }}
            >
              {results.score_label}
            </h2>

            {results.missed_questions.length > 0 && (
              <div className="kc-missed-section">
                <h3>📝 Review Missed Questions</h3>
                {results.missed_questions.map((missed, index) => (
                  <div key={index} className="kc-missed-card">
                    <div className="kc-missed-question">
                      <strong>Q{index + 1}:</strong> {missed.question}
                    </div>
                    <div className="kc-missed-answers">
                      <div className="kc-your-answer">
                        <span className="kc-label">Your Answer:</span>
                        <span className="kc-wrong">❌ {missed.your_answer}</span>
                      </div>
                      <div className="kc-correct-answer">
                        <span className="kc-label">Correct Answer:</span>
                        <span className="kc-correct">✅ {missed.correct_answer}</span>
                      </div>
                    </div>
                    <div className="kc-explanation">
                      <strong>📚 Educational Explanation:</strong>
                      <p>{missed.explanation}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {results.missed_questions.length === 0 && (
              <div className="kc-perfect-score">
                <span className="kc-perfect-icon">🎉</span>
                <p>Perfect Score! You answered all questions correctly.</p>
              </div>
            )}

            <div className="kc-results-reminder">
              <p>
                <strong>⚠️ Training Reminder:</strong> {results.training_reminder}
              </p>
            </div>

            <div className="kc-results-actions">
              <button className="btn-retry" onClick={() => startQuiz(selectedTopic.id)}>
                🔄 Retry This Topic
              </button>
              <button className="btn-new-topic" onClick={resetQuiz}>
                📚 Choose Another Topic
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Quiz Screen
  const currentQuestion = questions[currentQuestionIndex];
  const answeredCount = Object.keys(answers).length;
  const allAnswered = answeredCount === questions.length;

  return (
    <div className="knowledge-check-page">
      <header className="kc-header">
        <button className="btn-back" onClick={resetQuiz}>
          ← Exit Quiz
        </button>
        <h1>📚 {selectedTopic.name}</h1>
        <div className="kc-mode-badge training">
          🟡 Training Mode
        </div>
      </header>

      <div className="kc-container">
        {loading ? (
          <div className="kc-loading">
            <span className="kc-spinner">⏳</span>
            <p>Loading questions...</p>
          </div>
        ) : (
          <>
            <div className="kc-progress">
              <div className="kc-progress-text">
                Question {currentQuestionIndex + 1} of {questions.length}
              </div>
              <div className="kc-progress-bar">
                <div 
                  className="kc-progress-fill"
                  style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
              <div className="kc-answered-count">
                {answeredCount}/{questions.length} answered
              </div>
            </div>

            {selectedTopic.isAIGenerated && (
              <div className="kc-ai-badge">
                🤖 AI-Generated Questions
              </div>
            )}

            <div className="kc-question-card">
              <h2 className="kc-question-text">{currentQuestion?.question}</h2>

              <div className="kc-options-list">
                {currentQuestion?.options.map((option, index) => (
                  <button
                    type="button"
                    key={index}
                    className={`kc-option-btn ${answers[currentQuestion.id] === index ? 'selected' : ''}`}
                    onClick={() => handleAnswerSelect(currentQuestion.id, index)}
                  >
                    <span className="kc-option-letter">
                      {String.fromCharCode(65 + index)}.
                    </span>
                    <span className="kc-option-text">{option}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="kc-navigation">
              <button 
                className="btn-nav-prev"
                onClick={goToPrevQuestion}
                disabled={currentQuestionIndex === 0}
              >
                ← Previous
              </button>

              <div className="kc-question-dots">
                {questions.map((q, index) => (
                  <button
                    key={index}
                    className={`kc-dot ${index === currentQuestionIndex ? 'active' : ''} ${answers[q.id] !== undefined ? 'answered' : ''}`}
                    onClick={() => setCurrentQuestionIndex(index)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              {currentQuestionIndex < questions.length - 1 ? (
                <button 
                  className="btn-nav-next"
                  onClick={goToNextQuestion}
                >
                  Next →
                </button>
              ) : (
                <button 
                  className="btn-submit-quiz"
                  onClick={submitQuiz}
                  disabled={!allAnswered || loading}
                >
                  {loading ? 'Submitting...' : 'Submit Quiz ✓'}
                </button>
              )}
            </div>

            {!allAnswered && currentQuestionIndex === questions.length - 1 && (
              <p className="kc-incomplete-warning">
                ⚠️ Please answer all questions before submitting.
              </p>
            )}
          </>
        )}

        {error && <div className="kc-error">{error}</div>}
      </div>
    </div>
  );
}

export default KnowledgeCheck;
